import {
  ArenaGameState,
  CardGameType,
  SeatPosition,
  GamePhase,
  TrumpSuit
} from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { DEFAULT_TIMING } from '../../engine/tableConfig/defaults';
import {
  IGameModule,
  GameTerminology,
  GameLabels,
  GameIcons,
  GameDisplayIndicators,
  GamePresentationResult,
  BotTurnResolution,
  BotTurnTimingContext
} from '../types';
import { GamePresentation } from '../../presentation/GamePresentationTypes';
import { extractNormalizedSeatsList } from '../common/seatUtils';

// Presentation components
import { SpadesTableHud } from './presentation/SpadesTableHud';
import { SpadesCelebrationPill } from './presentation/SpadesCelebrationPill';
import { SpadesCelebrationOverlay } from './presentation/SpadesCelebrationOverlay';
import { SpadesBiddingHud } from '../../components/Auction/SpadesBiddingHud';
import { SpadesScorePresenter } from './SpadesScorePresenter';
import { SpadesContractFormatter } from './SpadesContractFormatter';
import { SpadesEventSynthesizer } from './events/SpadesEventSynthesizer';
import { StandardHandSortingStrategy } from '../common/StandardHandSortingStrategy';

export const SPADES_PRESENTATION: GamePresentation = {
  Hud: SpadesTableHud,
  CelebrationPill: SpadesCelebrationPill,
  CelebrationOverlay: SpadesCelebrationOverlay,
  phases: {
    BIDDING: SpadesBiddingHud,
  },
  events: {},
};

/**
 * Spades Game Module: Reference implementation of Client-Side Game Knowledge + Presentation.
 *
 * ARCHITECTURAL PRINCIPLE:
 * THE SERVER IS THE ONLY SOURCE OF TRUTH.
 *
 * The Spades client module:
 *  - Understands Spades terminology, labels, and icons/theme.
 *  - Selects Spades presentation components (SpadesContractHud, SpadesBiddingHud).
 *  - Formats authoritative contract summaries and outcome text.
 *  - Projects authoritative server state into display indicators WITHOUT calculating authoritative facts.
 *
 * The Spades client module MUST NEVER:
 *  - Calculate authoritative scores, trick winners, or sandbag penalties.
 *  - Determine legal plays or card ownership.
 *  - Determine whether Spades is legally broken.
 *  - Independently recreate Spades rules or mutate authoritative game state.
 *
 * If client and server ever disagree:
 * SERVER STATE WINS.
 */
export class SpadesGameModule implements IGameModule {
  public readonly gameType: CardGameType = 'SPADES';
  public readonly displayName: string = 'Spades';
  public readonly aliases: readonly string[] = [
    '500_PTS',
    '250_PTS',
    '300_PTS'
  ];
  public readonly scorePresenter = new SpadesScorePresenter();
  public readonly contractFormatter = new SpadesContractFormatter();
  public readonly handSortingStrategy = new StandardHandSortingStrategy();
  public readonly presentation: GamePresentation = SPADES_PRESENTATION;
  public readonly eventSynthesizer = new SpadesEventSynthesizer();

  // Spades Specific Features & Contracts
  public readonly hasBrokenTrumpRule = true;
  public readonly defaultTrumpSuit: TrumpSuit = 'Spades';
  public readonly canEndHandEarlyOnSet = false;
  public readonly hasKitty = false;
  public readonly hasTrumpDeclaration = false;
  public readonly hasKittyDiscard = false;

  public getPhaseTimeoutSec(phase: GamePhase, config?: GameTableConfig | null): number {
    const timing = config?.timing ?? DEFAULT_TIMING;
    switch (phase) {
      case 'BIDDING':
        return timing.biddingTimeoutSec;
      case 'PLAYING':
      default:
        return timing.turnTimeoutSec;
    }
  }

  public getBotDelayMs(phase: GamePhase, config?: GameTableConfig | null): number {
    const timing = config?.timing ?? DEFAULT_TIMING;
    switch (phase) {
      case 'BIDDING':
        return timing.botBiddingDelayMs;
      case 'PLAYING':
      default:
        return timing.botPlayDelayMs;
    }
  }

  public getTeamForSeat(seat: SeatPosition): 1 | 2 {
    return (seat === 0 || seat === 2) ? 1 : 2;
  }

  public getPartnerSeat(seat: SeatPosition): SeatPosition {
    return ((seat + 2) % 4) as SeatPosition;
  }

  public getPlayerCount(): number {
    return 4;
  }

  public isPrePlayPhase(phase: GamePhase): boolean {
    return phase === 'LOBBY' || phase === 'DEALING' || phase === 'BIDDING';
  }

  public isSetupPhase(phase: GamePhase): boolean {
    return phase === 'DEALING' || phase === 'BIDDING';
  }

  public isActiveGameplayPhase(phase: GamePhase): boolean {
    return phase === 'BIDDING' || phase === 'PLAYING';
  }

  public readonly terminology: GameTerminology = {
    trickNoun: 'trick',
    trickNounPlural: 'tricks',
    bidNoun: 'bid',
    trumpNoun: 'trump',
    penaltyNoun: 'bags',
    sweepNoun: 'Boston',
    partnerNoun: 'partner'
  };

  public readonly labels: GameLabels = {
    displayName: 'Spades',
    scoreboardTitle: 'Spades Partnership Scoreboard',
    team1Label: 'Team 1',
    team2Label: 'Team 2',
    winningBidLabel: 'Team Bids',
    contractSummaryLabel: 'Contracts',
    phaseDisplayNames: {
      LOBBY: 'Table Lobby',
      DEALING: 'Dealing 13 Cards',
      BIDDING: 'Partnership Bidding',
      PLAYING: 'Playing Trick',
      TRICK_END: 'Trick Evaluation',
      TRICK_RESOLVED: 'Trick Complete',
      HAND_END: 'Hand Complete',
      GAME_OVER: 'Match Concluded'
    }
  };

  public readonly icons: GameIcons = {
    themeSymbol: '♠',
    themeColor: 'purple',
    iconType: 'spade',
    statusBadges: {
      nil: '🛡️',
      bags: '🎒',
      spadesBroken: '♠'
    }
  };

  public getDisplayIndicators(
    state: ArenaGameState,
    _config?: GameTableConfig | null
  ): GameDisplayIndicators {
    // Read bids and nil bidders from authoritative server state
    const rawBids = state.bids || [];
    const nilBidders: SeatPosition[] = [];
    let t1Bid = 0;
    let t2Bid = 0;

    for (const b of rawBids) {
      if (!b || b.seat === undefined || b.seat === null) continue;
      const seat = Number(b.seat) as SeatPosition;
      if (b.bid === 0) {
        nilBidders.push(seat);
      }
      if (seat === 0 || seat === 2) {
        t1Bid += b.bid || 0;
      } else {
        t2Bid += b.bid || 0;
      }
    }

    // Prefer authoritative team target bids if already established by server booksToSet
    const bts = state.booksToSet;
    const finalT1Bid = bts?.team1?.target ?? bts?.team1?.bid ?? t1Bid;
    const finalT2Bid = bts?.team2?.target ?? bts?.team2?.bid ?? t2Bid;

    // Bags and books won from authoritative server state
    const t1Bags = state.team1Bags ?? (state.gameExt as any)?.team1Bags ?? 0;
    const t2Bags = state.team2Bags ?? (state.gameExt as any)?.team2Bags ?? 0;
    const spadesBroken = Boolean(state.spadesBroken ?? (state.gameExt as any)?.spadesBroken);

    return {
      trickWinnerSeat: (state.trickWinnerSeat !== undefined && state.trickWinnerSeat !== null)
        ? (Number(state.trickWinnerSeat) as SeatPosition)
        : null,
      isGameOver: Boolean(state.phase === 'GAME_OVER' || (state as any).isGameOver),
      matchWinner: state.matchWinner != null ? Number(state.matchWinner) : null,
      leadSuit: state.currentTrick && state.currentTrick.length > 0 && state.currentTrick[0]?.card
        ? state.currentTrick[0].card.suit
        : null,
      currentTurnSeat: (state.currentTurnSeat !== undefined && state.currentTurnSeat !== null)
        ? (Number(state.currentTurnSeat) as SeatPosition)
        : null,
      spades: {
        team1Bid: finalT1Bid,
        team2Bid: finalT2Bid,
        team1TricksWon: state.team1Books ?? 0,
        team2TricksWon: state.team2Books ?? 0,
        team1Bags: t1Bags,
        team2Bags: t2Bags,
        isSandbagRiskTeam1: t1Bags >= 7,
        isSandbagRiskTeam2: t2Bags >= 7,
        spadesBroken,
        nilBidders
      }
    };
  }

  public getPresentation(
    state: ArenaGameState,
    config?: GameTableConfig | null
  ): GamePresentationResult {
    const indicators = this.getDisplayIndicators(state, config);
    const contractSummary = this.formatContract(state, config);
    const outcomeText = this.formatOutcome(state, config);

    return {
      ...this.presentation,
      contractSummary,
      outcomeText,
      indicators
    };
  }

  public formatContract(state: ArenaGameState, config?: GameTableConfig | null): string {
    return this.contractFormatter.getContractSummaryText(state, config ?? undefined) || 'Spades Contract';
  }

  public formatOutcome(state: ArenaGameState, config?: GameTableConfig | null, context?: any): string {
    return this.contractFormatter.formatContractOutcome(state, context, config ?? undefined);
  }

  public mapRealtimeState(raw: unknown): Record<string, unknown> {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return {};
    }
    const data = raw as Record<string, unknown>;

    const nilBidders: number[] = Array.isArray(data.nilBidders)
      ? data.nilBidders.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
      : [];

    return {
      bids: Array.isArray(data.bids) ? data.bids : [],
      team1Bid: typeof data.team1Bid === 'number' && Number.isFinite(data.team1Bid) ? data.team1Bid : 0,
      team2Bid: typeof data.team2Bid === 'number' && Number.isFinite(data.team2Bid) ? data.team2Bid : 0,
      team1Bags: typeof data.team1Bags === 'number' && Number.isFinite(data.team1Bags) ? data.team1Bags : 0,
      team2Bags: typeof data.team2Bags === 'number' && Number.isFinite(data.team2Bags) ? data.team2Bags : 0,
      spadesBroken: typeof data.spadesBroken === 'boolean' ? data.spadesBroken : false,
      nilBidders,
      ...(data.booksToSet !== undefined ? { booksToSet: data.booksToSet } : {}),
      ...(data.blindNilBidders !== undefined ? { blindNilBidders: data.blindNilBidders } : {}),
    };
  }

  public getBotTurn(state: ArenaGameState): BotTurnResolution | null {
    if (!state) return null;
    const seatsList = extractNormalizedSeatsList(state.seats);
    const phase = state.phase;

    let activeSeat: SeatPosition | null = null;
    let actionType: string | undefined;

    if (phase === 'BIDDING') {
      if (state.currentBidderSeat !== null && state.currentBidderSeat !== undefined) {
        activeSeat = Number(state.currentBidderSeat) as SeatPosition;
        actionType = 'BID';
      }
    } else if (phase === 'PLAYING') {
      if (state.currentTurnSeat !== null && state.currentTurnSeat !== undefined) {
        activeSeat = Number(state.currentTurnSeat) as SeatPosition;
        actionType = 'PLAY';
      }
    }

    if (activeSeat === null || !seatsList?.[Number(activeSeat)]?.isBot) {
      return null;
    }

    const turnKey = `${state.handNumber}_${phase}_${activeSeat}_${state.completedTricks?.length || 0}_${state.currentTrick?.length || 0}_${state.bids?.length || 0}`;

    return {
      activeSeat,
      phase,
      turnKey,
      actionType
    };
  }

  public getBotTurnDelayMs(state: ArenaGameState, context?: BotTurnTimingContext): number {
    const timing = context?.config?.timing ?? DEFAULT_TIMING;
    const phase = state.phase;

    if (phase === 'BIDDING') {
      return timing.botBiddingDelayMs;
    }
    if (phase === 'PLAYING') {
      const completedCount = state.completedTricks?.length || 0;

      const isLeadingNewTrick = (state.currentTrick?.length || 0) === 0 || (state.currentTrick?.length || 0) >= 4;
      const isLeadingAfterCompletedTrick = isLeadingNewTrick && completedCount > 0;

      if (isLeadingAfterCompletedTrick) {
        const timeSinceTrickEnd = Date.now() - (context?.lastTrickEndTime || 0);
        const trickEndGracePeriod = Math.max(0, 960 - timeSinceTrickEnd);
        return Math.max(timing.botPlayDelayMs, 360, trickEndGracePeriod);
      }

      if (isLeadingNewTrick) {
        return Math.min(timing.botPlayDelayMs, 360);
      }

      // Mid-trick plays: steady metronome cadence measured from when the previous card landed
      const timeSinceLastCard = context?.lastCardPlayTime ? Math.max(0, Date.now() - context.lastCardPlayTime) : 0;
      const targetDispatchCadence = 200;
      return Math.max(50, targetDispatchCadence - timeSinceLastCard);
    }
    return timing.botPlayDelayMs;
  }

  public getLastActorSeat(state: ArenaGameState): SeatPosition | null {
    const phase = state.phase;
    if (phase === 'BIDDING') {
      if (state.bids && state.bids.length > 0) {
        return Number(state.bids[state.bids.length - 1].seat) as SeatPosition;
      }
      return null;
    }

    const comp = state.completedTricks || [];
    const latestComp = comp.length > 0 ? comp[comp.length - 1] : null;
    const trickSeat = (state.currentTrick && state.currentTrick.length > 0)
      ? Number(state.currentTrick[state.currentTrick.length - 1].seat)
      : (latestComp?.winnerSeat !== null && latestComp?.winnerSeat !== undefined)
        ? Number(latestComp.winnerSeat)
        : (latestComp?.winningSeat !== null && latestComp?.winningSeat !== undefined)
          ? Number(latestComp.winningSeat)
          : (state.trickWinnerSeat !== null && state.trickWinnerSeat !== undefined)
            ? Number(state.trickWinnerSeat)
            : null;

    return trickSeat !== null && !isNaN(trickSeat) ? (trickSeat as SeatPosition) : null;
  }
}

export const spadesModule = new SpadesGameModule();
